import { Bell, BellRing } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@opencloud/ui";
import { useCallback, useEffect, useState } from "react";
import { api, requestBody } from "../api.js";
import { agentBrand } from "../brand.js";

const STATUS_EVENT = "opencloud:agent-notifications-changed";
const PROMPTED_KEY = "opencloud.agentNotificationsPrompted";

type NotificationStatus =
  | "checking"
  | "enabled"
  | "disabled"
  | "blocked"
  | "unsupported";

export function AgentNotifications({
  notify,
}: {
  notify?: (message: string) => void;
}) {
  const status = useNotificationStatus();
  if (status === "unsupported" || status === "checking") return null;
  const enabled = status === "enabled";

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="a3-icon-button"
          />
        }
        aria-label={
          enabled
            ? "Turn off agent notifications"
            : "Notify me when the agent finishes"
        }
        aria-pressed={enabled}
        onClick={() => void changeNotifications(!enabled, notify)}
      >
        {enabled ? <BellRing aria-hidden="true" /> : <Bell aria-hidden="true" />}
      </TooltipTrigger>
      <TooltipContent>
        {enabled ? "Completion notifications on" : statusLabel(status)}
      </TooltipContent>
    </Tooltip>
  );
}

export function AgentNotificationSettings({
  notify,
}: {
  notify: (message: string) => void;
}) {
  const status = useNotificationStatus();
  const [testing, setTesting] = useState(false);
  const enabled = status === "enabled";

  async function sendTestNotification() {
    setTesting(true);
    try {
      const result = await api<{ sent: boolean }>("/v1/push/test", {
        method: "POST",
        body: requestBody(),
      });
      notify(
        result.sent
          ? "Test notification sent."
          : "No active notification subscription was found for this device.",
      );
    } catch {
      notify("The test notification could not be sent.");
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="a3-agent-settings-card" data-notification-status={status}>
      <strong>Completion notifications</strong>
      <p>{settingsDescription(status)}</p>
      {status !== "unsupported" ? (
        <div className="a3-agent-settings-actions">
          <Button
            type="button"
            variant="outline"
            className="a3-action a3-action-secondary"
            disabled={status === "checking" || status === "blocked"}
            onClick={() => void changeNotifications(!enabled, notify)}
          >
            {enabled ? "Disable notifications" : "Enable notifications"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="a3-action"
            disabled={!enabled || testing}
            onClick={() => void sendTestNotification()}
          >
            <BellRing aria-hidden="true" />
            {testing ? "Sending…" : "Send test notification"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function AgentNotificationPrompt({
  open,
  onOpenChange,
  notify,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notify: (message: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="a3-notification-prompt">
        <DialogHeader>
          <DialogTitle>Know when your agent is done</DialogTitle>
          <DialogDescription>
            {`Enable notifications on this device and ${agentBrand().productName} will let you know when the agent has a new response. Opening one returns to the right conversation.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
          <Button
            type="button"
            onClick={() => {
              void changeNotifications(true, notify).then((status) => {
                if (status === "enabled") onOpenChange(false);
              });
            }}
          >
            Enable notifications
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export async function shouldOfferAgentNotifications(): Promise<boolean> {
  if (typeof window === "undefined" || !notificationsSupported()) return false;
  try {
    if (window.localStorage.getItem(PROMPTED_KEY) === "true") return false;
    const status = await readNotificationStatus();
    if (status !== "disabled") return false;
    window.localStorage.setItem(PROMPTED_KEY, "true");
    return true;
  } catch {
    return false;
  }
}

function useNotificationStatus(): NotificationStatus {
  const [status, setStatus] = useState<NotificationStatus>("checking");
  const refresh = useCallback(() => {
    void readNotificationStatus().then(setStatus);
  }, []);
  useEffect(() => {
    refresh();
    window.addEventListener(STATUS_EVENT, refresh);
    return () => window.removeEventListener(STATUS_EVENT, refresh);
  }, [refresh]);
  return status;
}

async function changeNotifications(
  enabled: boolean,
  notify?: (message: string) => void,
): Promise<NotificationStatus> {
  try {
    if (!notificationsSupported()) return "unsupported";
    if (enabled && Notification.permission !== "granted") {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        notify?.("Notifications were not enabled.");
        dispatchStatusChange();
        return permission === "denied" ? "blocked" : "disabled";
      }
    }
    const registration = await navigator.serviceWorker.register("/push-worker.js", {
      scope: "/",
    });
    const current = await registration.pushManager.getSubscription();
    if (!enabled) {
      if (current) {
        await api("/v1/push/subscription", {
          method: "DELETE",
          body: requestBody({ endpoint: current.endpoint }),
        });
        await current.unsubscribe();
      }
      notify?.("Agent completion notifications turned off.");
      dispatchStatusChange();
      return "disabled";
    }
    if (current) {
      dispatchStatusChange();
      return "enabled";
    }
    const config = await api<{ applicationServerKey: string }>("/v1/push/config");
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeKey(config.applicationServerKey),
    });
    await api("/v1/push/subscription", {
      method: "PUT",
      body: JSON.stringify(subscription.toJSON()),
    });
    notify?.("You’ll be notified when the agent finishes.");
    dispatchStatusChange();
    return "enabled";
  } catch {
    notify?.("Agent notifications could not be changed.");
    dispatchStatusChange();
    return readNotificationStatus();
  }
}

async function readNotificationStatus(): Promise<NotificationStatus> {
  if (!notificationsSupported()) return "unsupported";
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    if (await registration?.pushManager.getSubscription()) return "enabled";
    return Notification.permission === "denied" ? "blocked" : "disabled";
  } catch {
    return "disabled";
  }
}

function notificationsSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function dispatchStatusChange(): void {
  window.dispatchEvent(new Event(STATUS_EVENT));
}

function statusLabel(status: NotificationStatus): string {
  return status === "blocked"
    ? "Notifications blocked in browser settings"
    : "Notify when finished";
}

function settingsDescription(status: NotificationStatus): string {
  if (status === "enabled")
    return "Enabled on this device. A notification opens the conversation that finished.";
  if (status === "blocked")
    return "Blocked by this browser. Allow notifications in the browser’s site settings to enable them.";
  if (status === "unsupported")
    return "Web push notifications are not available in this browser.";
  if (status === "checking") return "Checking this device…";
  return "Disabled on this device. Enable them to be notified when Agent finishes.";
}

function decodeKey(value: string): Uint8Array<ArrayBuffer> {
  const bytes = Uint8Array.from(
    atob(value.replace(/-/g, "+").replace(/_/g, "/")),
    (character) => character.charCodeAt(0),
  );
  return bytes as Uint8Array<ArrayBuffer>;
}
